import React, { useEffect, useRef, useState } from "react";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { years } from "./DateQuarterPicker";

const quartersOptions: { title: string; id: number }[] = [
  { title: "Q1: Jan, Feb, Mar", id: 1 },
  { title: "Q2: Apr, May, Jun", id: 2 },
  { title: "Q3: Jul, Aug, Sep", id: 3 },
  { title: "Q4: Oct, Nov, Dec", id: 4 }
];

interface Props {
  handleClose: () => void;
  onSelect: (year: number, quarter: number) => void;
  quarterNumber: number | null;
  yearNumber: number;
}

export const PopoverContent: React.FC<Props> = ({ handleClose, onSelect, quarterNumber, yearNumber }) => {
  const [year, setYear] = useState<number>(yearNumber);
  const [quarter, setQuarter] = useState<number | null>(quarterNumber);

  const [isYearPick, setIsYearPick] = useState<boolean>(false);

  const selectedYearRef = useRef<HTMLButtonElement | null>(null);
  const isFirstRender: React.MutableRefObject<boolean> = useRef(true);

  // Dispatch data after select year and quarter
  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (quarter) {
      onSelect(year, quarter);
    }
  }, [year, quarter]);

  const handleOpenYearPicker = () => {
    setIsYearPick((prevState) => !prevState);
    setTimeout(() => {
      selectedYearRef.current?.scrollIntoView({ behavior: "instant", block: "center" });
    }, 0);
  };

  const handleYearPick = (year: number) => {
    setIsYearPick(false);
    setYear(year);
  };

  const handleShiftYear = (increment: boolean) => {
    let result = year;
    if (increment) {
      result = year + 1;
    } else {
      result = year - 1;
    }
    if (!years.includes(result)) return;

    setYear(result);
  };

  const handleQuarterPick = (id: number) => {
    setQuarter(id);
    handleClose();
  };

  return (
    <Stack
      sx={{
        width: 240,
        maxHeight: 236,
        minHeight: 236,
        bgcolor: "white",
        overflow: "hidden"
      }}
    >
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "12px",
          mb: "4px",
          pl: "24px",
          pr: "12px",
          maxHeight: "40px",
          minHeight: "40px"
        }}
      >
        <Stack sx={{ flexDirection: "row", alignItems: "center" }}>
          <Typography
            onClick={handleOpenYearPicker}
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              cursor: "pointer"
            }}
          >
            {year}
          </Typography>
          <IconButton onClick={handleOpenYearPicker}>
            <ArrowDropDownIcon />
          </IconButton>
        </Stack>
        <Stack sx={{ flexDirection: "row" }}>
          <IconButton onClick={() => handleShiftYear(false)}>
            <ArrowBackIosIcon />
          </IconButton>
          <IconButton onClick={() => handleShiftYear(true)}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Stack>
      </Stack>
      {isYearPick ? (
        <Stack sx={{ flexDirection: "row", flexWrap: "wrap", p: "0 12px 10px 14px", overflow: "auto" }}>
          {years.map((yearOption) => (
            <Button
              key={yearOption}
              onClick={() => handleYearPick(yearOption)}
              {...(yearOption === year && { ref: selectedYearRef, variant: "contained" })}
            >
              {yearOption}
            </Button>
          ))}
        </Stack>
      ) : (
        <Stack sx={{ alignItems: "center" }}>
          {quartersOptions.map(({ title, id }) => (
            <Button key={id} {...(quarter === id && { variant: "contained" })} onClick={() => handleQuarterPick(id)}>
              {title}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
